import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Users, MapPin, CreditCard, MessageCircle, AlertTriangle, Lock, BookOpen, Flag, Mail } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const Safety = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-warm">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          ← Back
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Safety Policy</h1>
        </div>

        <Card className="p-8 shadow-warm border-border/50 space-y-6">
          <section>
            <p className="text-muted-foreground mb-4">
              Last updated: {new Date().toLocaleDateString()}
            </p>
            <p>
              imata is committed to creating a safe environment for all users, with special 
              attention to protecting children and teenagers. This policy outlines our protection 
              practices and guidelines for younger users.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Age Requirements
            </h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li><strong>Minimum age:</strong> Users must be at least 13 years old</li>
              <li><strong>Parental consent:</strong> Users aged 13-17 require parental or guardian consent</li>
              <li>We may request age verification at registration or any time during use</li>
              <li>Accounts found to be underage will be immediately suspended</li>
              <li>Parents/guardians may request account removal for minors at any time</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Lock className="w-5 h-5 text-secondary" />
              Account Safety
            </h2>
            <p className="text-muted-foreground">We implement robust account security measures:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Password protection with secure encryption</li>
              <li>Automatic session timeout after periods of inactivity</li>
              <li>Biometric authentication options (fingerprint/face ID)</li>
              <li>Email verification required for all accounts</li>
              <li>Suspicious login detection and alerts</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Location Privacy
            </h2>
            <p className="text-muted-foreground">Your location is protected:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Only approximate location is shown to other users (never exact address)</li>
              <li>Location sharing can be disabled at any time</li>
              <li>Automatic location timeout when app is backgrounded</li>
              <li>Parents can request location features be disabled for minor accounts</li>
              <li>No real-time tracking or location history storage</li>
              <li>Customizable visibility radius limits</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-secondary" />
              Payment Safety
            </h2>
            <p className="text-muted-foreground">Financial protections include:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Maximum gift amount limits to prevent excessive spending</li>
              <li>Rate limiting on gift sending (one gift per hour)</li>
              <li>All payments processed through PCI-compliant secure providers</li>
              <li>No payment card details stored in our systems</li>
              <li>Parents can contact us to set additional spending restrictions for minor accounts</li>
              <li>Clear transaction history for monitoring</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              Interaction Safety
            </h2>
            <p className="text-muted-foreground">Safe interactions are our priority:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Gift messages are limited in length and reviewed for inappropriate content</li>
              <li>Users can block or report any account</li>
              <li>Anonymous gifting available to protect sender identity</li>
              <li>No direct messaging or chat features between users</li>
              <li>All interactions are logged for safety review if needed</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Prohibited Behaviors
            </h2>
            <p className="text-muted-foreground">
              We have zero tolerance for harmful behavior:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Any inappropriate contact with or targeting of minors</li>
              <li>Harassment, bullying, or threatening behavior</li>
              <li>Grooming, solicitation, or predatory behavior</li>
              <li>Sharing explicit, violent, or harmful content</li>
              <li>Impersonation or creating fake accounts</li>
              <li>Using the platform for illegal activities</li>
            </ul>
            <p className="text-muted-foreground mt-2 font-medium">
              Violations result in immediate account termination and may be reported to authorities.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-secondary" />
              Data Protection for Minors
            </h2>
            <p className="text-muted-foreground">Special protections for users under 18:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Minimal data collection—only what's necessary for the service</li>
              <li>No targeted advertising or data selling</li>
              <li>Parents/guardians can request access to their child's data</li>
              <li>Immediate data deletion available upon request</li>
              <li>Compliance with COPPA, GDPR, and other child protection regulations</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Parental Resources
            </h2>
            <p className="text-muted-foreground">For parents and guardians:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Request access to your child's account activity</li>
              <li>Set additional restrictions on your child's account</li>
              <li>Request immediate account deletion</li>
              <li>Report concerns directly to our safety team</li>
              <li>Receive notifications about significant account activity</li>
            </ul>
            <p className="text-muted-foreground mt-2">
              Contact us at <a href="mailto:parents@giftmap.app" className="text-primary hover:underline">parents@giftmap.app</a> for 
              parental controls and inquiries.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Flag className="w-5 h-5 text-destructive" />
              Reporting Concerns
            </h2>
            <p className="text-muted-foreground">
              If you witness or experience concerning behavior:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Use the in-app report feature to flag users or content</li>
              <li>Email our safety team at <a href="mailto:safety@giftmap.app" className="text-primary hover:underline">safety@giftmap.app</a></li>
              <li>For immediate danger, contact local law enforcement</li>
              <li>We respond to safety reports within 24 hours</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Mail className="w-5 h-5 text-accent" />
              Contact Our Safety Team
            </h2>
            <p className="text-muted-foreground">
              We're here to help with any safety concerns:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>General safety: <a href="mailto:safety@giftmap.app" className="text-primary hover:underline">safety@giftmap.app</a></li>
              <li>Parental inquiries: <a href="mailto:parents@giftmap.app" className="text-primary hover:underline">parents@giftmap.app</a></li>
              <li>Privacy concerns: <a href="mailto:privacy@giftmap.app" className="text-primary hover:underline">privacy@giftmap.app</a></li>
            </ul>
          </section>

          <section className="pt-4 border-t border-border space-y-3">
            <p className="text-sm text-muted-foreground">
              By using imata, you agree to follow this Safety Policy. We continuously update 
              our safety measures and will notify users of significant changes.
            </p>
            <p className="text-sm text-muted-foreground">
              Related policies:{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              {" | "}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            </p>
          </section>
        </Card>
      </div>
    </div>
  );
};

export default Safety;
